import React, { useState, useRef, useEffect } from 'react';
import { useControlSpec } from '../contexts/ControlSpecContext';
import TestStepsPreview from './TestStepsPreview';
import DetailedModal from './DetailedModal';
import { Send, Loader2, AlertCircle } from 'lucide-react';

const ChatInterface = () => {
    const { conversation, refineWithChat, isRefining, error, controlSpec } = useControlSpec();
    const [message, setMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [conversation]);

    const handleSend = async () => {
        if (!message.trim() || isRefining) return;

        await refineWithChat(message);
        setMessage('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!controlSpec) {
        return (
            <div className="flex items-center justify-center h-full text-gray-500">
                <p>No control spec loaded. Please generate control steps first.</p>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Chat Area (Left Side) */}
            <div className="w-1/2 flex flex-col p-6">
                {/* Preview Card */}
                <TestStepsPreview onViewDetails={() => setIsModalOpen(true)} />

                {/* Conversation Area */}
                <div className="flex-1 overflow-y-auto mb-4 space-y-4">
                    {conversation.length === 0 && (
                        <div className="text-center text-gray-500 mt-8">
                            <p className="text-sm">Start a conversation to refine your control test steps.</p>
                            <p className="text-xs mt-2">
                                Try: "Make step 2 more specific" or "Add more evidence requirements to step 3"
                            </p>
                        </div>
                    )}

                    {conversation.map((msg, i) => (
                        <div
                            key={i}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[80%] p-3 rounded-lg ${msg.role === 'user'
                                        ? 'bg-blue-600 text-white'
                                        : msg.role === 'error'
                                            ? 'bg-red-100 text-red-900 border border-red-200'
                                            : 'bg-white text-gray-900 border border-gray-200 shadow-sm'
                                    }`}
                            >
                                {msg.role === 'error' && (
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertCircle className="h-4 w-4" />
                                        <span className="font-medium text-sm">Error</span>
                                    </div>
                                )}
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                        </div>
                    ))}

                    {isRefining && (
                        <div className="flex justify-start">
                            <div className="bg-white border border-gray-200 p-3 rounded-lg flex items-center gap-2 text-gray-600">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-sm">AI is refining...</span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Refine test steps... (Press Enter to send, Shift+Enter for new line)"
                        className="w-full px-4 py-3 rounded-t-lg resize-none focus:outline-none text-sm"
                        rows={3}
                        disabled={isRefining}
                    />
                    <div className="px-4 py-2 bg-gray-50 rounded-b-lg flex justify-between items-center border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                            {message.length} characters
                        </span>
                        <button
                            onClick={handleSend}
                            disabled={isRefining || !message.trim()}
                            className={`px-4 py-2 rounded-md flex items-center gap-2 transition-colors text-sm font-medium ${isRefining || !message.trim()
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            <Send className="h-4 w-4" />
                            Send
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-800 text-xs">
                        {error}
                    </div>
                )}
            </div>

            {/* Detailed Modal (Right Side - Sliding) */}
            <DetailedModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
};

export default ChatInterface;
